import * as React from "react";
import { useThunkReducer } from "../hooks/useThunkReducer";

export type RootViewModel = {
  cache: {
    [url: string]: any;
  };
};

type RootViewState = RootViewModel;

type RootViewDispatch = {
  (action: RootViewActions): void;
  (
    action: (
      dispatch: React.Dispatch<RootViewActions>,
      getState: () => RootViewModel
    ) => void
  ): void;
};

const RootViewStateContext = React.createContext<RootViewState | undefined>(
  undefined
);

const RootViewGetStateContext = React.createContext<
  () => RootViewState | undefined
>(undefined);

const RootViewDispatchContext = React.createContext<
  RootViewDispatch | undefined
>(undefined);

interface ProviderProps {
  initialState: RootViewModel;
}

type RootViewActions =
  | {
      type: "ADD_VIEW_TO_CACHE";
      payload: {
        url: string;
        data: any;
      };
    }
  | {
      type: "UPDATE_CACHED_VIEW";
      payload: {
        url: string;
        data: any;
      };
    }
  | {
      type: "URL_VIEW_MODEL_ERROR";
      payload: {
        url: string;
        error: Error;
      };
    }
  | {
      type: "REMOVE_CACHED_VIEW";
      payload: {
        url: string;
      };
    }
  | {
      type: "REMOVE_CACHED_VIEWS";
      payload: string[];
    };

const RootViewReducer = (state: RootViewModel, action: RootViewActions) => {
  switch (action.type) {
    case "ADD_VIEW_TO_CACHE":
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.payload.url]: action.payload.data,
        },
      };
    case "UPDATE_CACHED_VIEW":
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.payload.url]: Object.assign(
            {},
            state.cache[action.payload.url],
            action.payload.data
          ),
        },
      };
    case "REMOVE_CACHED_VIEW":
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.payload.url]: undefined,
        },
      };
    case "REMOVE_CACHED_VIEWS": {
      return {
        ...state,
        cache: {
          ...state.cache,
          ...(() => {
            return action.payload.reduce((acc, cur) => {
              acc[cur] = undefined;
              return acc;
            }, {} as Record<string, undefined>);
          })(),
        },
      };
    }
    case "URL_VIEW_MODEL_ERROR":
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.payload.url]: action.payload.error,
        },
      };
    default:
      return state;
  }
};

export const RootViewProvider: React.FC<ProviderProps> = ({
  initialState,
  children,
}) => {
  if (!initialState) {
    throw Error("No initialState provided to RootViewStateContext.Provider");
  }

  const [getState, dispatch] = useThunkReducer(RootViewReducer, initialState);

  // if (process.env.NODE_ENV !== "production") {
  //   global.RootViewState = getState();
  // }

  return (
    <RootViewGetStateContext.Provider value={getState}>
      <RootViewDispatchContext.Provider value={dispatch}>
        <RootViewStateContext.Provider value={getState()}>
          {children}
        </RootViewStateContext.Provider>
      </RootViewDispatchContext.Provider>
    </RootViewGetStateContext.Provider>
  );
};

export const useRootViewGetState = () => {
  const getState = React.useContext(RootViewGetStateContext);

  if (!getState) {
    throw Error(
      "useRootViewGetState used outside a RootViewGetStateContext.Provider"
    );
  }

  return getState;
};

export const useRootViewState = () => {
  const state = React.useContext(RootViewStateContext);

  if (!state) {
    throw Error(
      "useRootViewState hook used outside a RootViewStateContext.Provider"
    );
  }

  return state;
};

export const useRootViewDispatch = () => {
  const dispatch = React.useContext(RootViewDispatchContext);

  if (!dispatch) {
    throw Error(
      "useRootViewDispatch hook used outside a RootViewDispatchContext.Provider"
    );
  }

  return dispatch;
};

export const useRootView = () => {
  const state = useRootViewState();
  const dispatch = useRootViewDispatch();
  const getState = useRootViewGetState();

  return [state, dispatch, getState] as const;
};
